import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountsUsersCountByState} from 'src/openapi/admin/apis/AccountsUsersCountsApi';
import {UserState} from 'src/openapi/admin/models/UserState';
import {useAppDispatch} from 'src/store/hooks';

import {
    accountSearchResultLicensedUsersActions,
    accountSearchResultLicensedUsersDataSelector,
    accountSearchResultLicensedUsersResultSelector,
} from '../slices/account-search-result-licensed-users.slice';
import {accountSearchResultStateSelector} from '../slices/account-search-result-state.slice';

import type {AccountSearchResultCacheKeys} from '../typedef';

type Props = {
    accountIds: number[],
    totalCount: number,
};

export const useGetAccountSearchResultLicensedUsers = ({accountIds, totalCount}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(() => apiDispatch(
        getAccountsUsersCountByState({
            getAccountsUsersCountsByStateRequest: {
                accountIds: [...new Set(accountIds)],
                userState: UserState.LICENSED,
            }}),
    ), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, number>,
        dataCacheKeys: AccountSearchResultCacheKeys,
    ) => {
        dispatch(accountSearchResultLicensedUsersActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    return useCachedTable({
        dataSelector: accountSearchResultLicensedUsersDataSelector,
        stateSelector: accountSearchResultStateSelector,
        resultSelector: accountSearchResultLicensedUsersResultSelector,
        actions: accountSearchResultLicensedUsersActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
    });
};
